import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AdminLayout from "@/pages/admin/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PasswordStrengthMeter, FieldError } from "@/components/FormFeedback";
import { toast } from "sonner";
import { HardDrive, CalendarClock, Users2, Activity, Building2 } from "lucide-react";
import { validateEmail, validateMobile, cleanMobile, validatePassword, validateConfirmPassword } from "@/lib/validation";
import { cn } from "@/lib/utils";

const STATUSES = ["ACTIVE", "EXPIRED"];

const SUBSCRIPTION_STATUS_BADGE = {
  ACTIVE: { label: "Active", className: "bg-green-100 text-green-700" },
  NOT_STARTED: { label: "Not Started Yet", className: "bg-slate-100 text-slate-600" },
  GRACE: { label: "Grace Period (Read-Only)", className: "bg-amber-100 text-amber-700" },
  LOCKED: { label: "Locked (Access Blocked)", className: "bg-red-100 text-red-700" },
};

export default function ClinicDetailPage() {
  const { id } = useParams();
  const [clinic, setClinic] = useState(null);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({});
  const [touched, setTouched] = useState({});
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [resetTouched, setResetTouched] = useState({});

  const load = () => api.get(`/admin/clinics/${id}`).then(({ data }) => {
    setClinic(data);
    setForm({
      clinic_name: data.clinic_name,
      doctor_name: data.doctor_name,
      specialization: data.specialization,
      mobile: data.mobile,
      email: data.email,
      address: data.address || "",
      subscription_plan: data.subscription_plan,
      status: data.status,
      subscription_start: data.subscription_start,
      subscription_end: data.subscription_end,
    });
  });

  useEffect(() => {
    load();
    api.get("/admin/plans").then(({ data }) => setPlans(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const markTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));

  const fieldErrors = {
    mobile: validateMobile(form.mobile),
    email: validateEmail(form.email),
    specialization: !form.specialization?.trim() ? "Specialization is required" : null,
  };
  const isValid = Object.values(fieldErrors).every((e) => !e);

  const saveChanges = async () => {
    setTouched({ mobile: true, email: true, specialization: true });
    if (!isValid) return;
    try {
      await api.put(`/admin/clinics/${id}`, {
        ...form,
        specialization: form.specialization.trim(),
        mobile: cleanMobile(form.mobile),
      });
      toast.success("Clinic updated successfully");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resetFieldErrors = {
    newPassword: validatePassword(newPassword),
    newPasswordConfirm: validateConfirmPassword(newPassword, newPasswordConfirm),
  };
  const resetIsValid = Object.values(resetFieldErrors).every((e) => !e);

  const resetDoctorPassword = async () => {
    setResetTouched({ newPassword: true, newPasswordConfirm: true });
    if (!resetIsValid) return;
    try {
      await api.post(`/admin/doctors/${clinic.doctor_id}/reset-password`, { new_password: newPassword });
      toast.success("Doctor password reset. They will be asked to change it on next login.");
      setResetOpen(false);
      setNewPassword("");
      setNewPasswordConfirm("");
      setResetTouched({});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!clinic) return <AdminLayout><p className="text-slate-500">Loading...</p></AdminLayout>;

  const currentPlan = plans.find((p) => p.plan_code === form.subscription_plan);
  const statusBadge = SUBSCRIPTION_STATUS_BADGE[clinic.subscription_status] || SUBSCRIPTION_STATUS_BADGE.ACTIVE;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="clinic-detail-heading">
            {clinic.clinic_name}
          </h1>
          <p className="text-sm text-slate-500 font-mono">{clinic.clinic_code} · Dr. {clinic.doctor_name} · {clinic.specialization}</p>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="clinic-detail-subscription-status" className={cn("rounded-full px-3 py-1 text-xs font-semibold", statusBadge.className)}>
            {statusBadge.label}
          </span>
          <Button data-testid="reset-doctor-password-btn" variant="outline" onClick={() => setResetOpen(true)}>
            Reset Doctor Password
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center gap-2 text-slate-600 text-sm"><Activity className="h-4 w-4" /> Appointments Used</div>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{clinic.appointments_used} / {clinic.appointments_limit}</p>
        </div>
        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center gap-2 text-slate-600 text-sm"><Users2 className="h-4 w-4" /> Staff Used</div>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{clinic.staff_used} / {clinic.max_staff}</p>
        </div>
        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center gap-2 text-slate-600 text-sm"><HardDrive className="h-4 w-4" /> Storage Used</div>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{(clinic.storage_used_bytes / 1024 / 1024).toFixed(2)} MB</p>
        </div>
      </div>

      <div className="mt-6 max-w-3xl card-surface card-surface-hover rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Building2 className="h-4 w-4" /> Clinic Information</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Clinic Name</Label>
            <Input data-testid="clinic-detail-clinic-name" value={form.clinic_name} onChange={(e) => set("clinic_name", e.target.value)} />
          </div>
          <div>
            <Label>Clinic Code</Label>
            <Input data-testid="clinic-detail-clinic-code" disabled value={clinic.clinic_code} className="bg-slate-50 text-slate-500 font-mono" />
          </div>
          <div>
            <Label>Doctor Name</Label>
            <Input data-testid="clinic-detail-doctor-name" value={form.doctor_name} onChange={(e) => set("doctor_name", e.target.value)} />
          </div>
          <div>
            <Label>Specialization</Label>
            <Input
              data-testid="clinic-detail-specialization-input"
              value={form.specialization}
              placeholder="e.g. Cardiologist, Pediatrician, ENT Specialist"
              onBlur={() => markTouched("specialization")}
              className={cn(touched.specialization && fieldErrors.specialization && "border-red-500 focus-visible:ring-red-500")}
              onChange={(e) => set("specialization", e.target.value)}
            />
            {touched.specialization && <FieldError testId="clinic-detail-specialization-error" message={fieldErrors.specialization} />}
          </div>
          <div>
            <Label>Mobile</Label>
            <Input data-testid="clinic-detail-mobile" value={form.mobile} onBlur={() => markTouched("mobile")} className={cn(touched.mobile && fieldErrors.mobile && "border-red-500 focus-visible:ring-red-500")} onChange={(e) => set("mobile", e.target.value.replace(/[^\d]/g, "").slice(0, 10))} />
            {touched.mobile && <FieldError testId="clinic-detail-mobile-error" message={fieldErrors.mobile} />}
          </div>
          <div>
            <Label>Email (Doctor login)</Label>
            <Input data-testid="clinic-detail-email" type="email" value={form.email} onBlur={() => markTouched("email")} className={cn(touched.email && fieldErrors.email && "border-red-500 focus-visible:ring-red-500")} onChange={(e) => set("email", e.target.value)} />
            {touched.email && <FieldError testId="clinic-detail-email-error" message={fieldErrors.email} />}
          </div>
          <div className="sm:col-span-2">
            <Label>Clinic Address</Label>
            <Input data-testid="clinic-detail-address" value={form.address} onChange={(e) => set("address", e.target.value)} />
          </div>
        </div>
      </div>

      <div className="mt-6 max-w-3xl card-surface card-surface-hover rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Subscription Management</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Plan</Label>
            <Select value={form.subscription_plan} onValueChange={(v) => setForm((f) => ({ ...f, subscription_plan: v }))}>
              <SelectTrigger data-testid="clinic-detail-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {plans.filter((p) => p.is_active || p.plan_code === clinic.subscription_plan).map((p) => (
                  <SelectItem key={p.plan_code} value={p.plan_code}>{p.label}{!p.is_active ? " (Inactive)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="clinic-detail-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Max Staff (determined by plan)</Label>
            <Input
              data-testid="clinic-detail-max-staff"
              disabled
              value={currentPlan ? `${currentPlan.max_staff} Active Staff Account${currentPlan.max_staff > 1 ? "s" : ""}` : `${clinic.max_staff} Active Staff Account${clinic.max_staff > 1 ? "s" : ""}`}
              className="bg-slate-50 text-slate-500"
            />
          </div>
          <div>
            <Label>Start Date</Label>
            <DatePicker testId="clinic-detail-start-date" value={form.subscription_start} onChange={(v) => setForm((f) => ({ ...f, subscription_start: v }))} />
          </div>
          <div>
            <Label>Expiry Date</Label>
            <DatePicker testId="clinic-detail-expiry" value={form.subscription_end} onChange={(v) => setForm((f) => ({ ...f, subscription_end: v }))} />
          </div>
        </div>
        <Button data-testid="clinic-detail-save-btn" disabled={!isValid} className="mt-4 bg-teal-700 hover:bg-teal-800" onClick={saveChanges}>
          Save Changes
        </Button>
      </div>

      <Dialog open={resetOpen} onOpenChange={(o) => { setResetOpen(o); if (!o) { setNewPassword(""); setNewPasswordConfirm(""); setResetTouched({}); } }}>
        <DialogContent data-testid="reset-doctor-password-modal">
          <DialogHeader>
            <DialogTitle>Reset Doctor Password</DialogTitle>
            <DialogDescription>Set a new temporary password for this doctor. They will be asked to change it on next login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New Temporary Password</Label>
              <PasswordInput testId="reset-doctor-password-input" value={newPassword} onBlur={() => setResetTouched((t) => ({ ...t, newPassword: true }))} className={cn(resetTouched.newPassword && resetFieldErrors.newPassword && "border-red-500 focus-visible:ring-red-500")} onChange={(e) => setNewPassword(e.target.value)} />
              <PasswordStrengthMeter password={newPassword} />
              {resetTouched.newPassword && <FieldError testId="reset-doctor-password-error" message={resetFieldErrors.newPassword} />}
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <PasswordInput testId="reset-doctor-password-confirm-input" value={newPasswordConfirm} onBlur={() => setResetTouched((t) => ({ ...t, newPasswordConfirm: true }))} className={cn(resetTouched.newPasswordConfirm && resetFieldErrors.newPasswordConfirm && "border-red-500 focus-visible:ring-red-500")} onChange={(e) => setNewPasswordConfirm(e.target.value)} />
              {resetTouched.newPasswordConfirm && <FieldError testId="reset-doctor-password-confirm-error" message={resetFieldErrors.newPasswordConfirm} />}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800" disabled={!resetIsValid} data-testid="reset-doctor-password-submit-btn" onClick={resetDoctorPassword}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
